import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, UI } from "@/i18n/lang";
import { BRAND, LOGO } from "@/content/site";
import { LocaleLink as Link, LocaleNavLink as NavLink } from "@/i18n/routes";

const navItems = [
  { to: "/work", key: "work" },
  { to: "/services", key: "services" },
  { to: "/products", key: "products" },
  { to: "/contact", key: "contact" },
];

const Logo = () => (
  <Link to="/" data-testid="logo-home" className="flex items-center gap-3 group">
    <img
      src={LOGO.badge}
      alt="WERKVERDUNG"
      className="h-9 w-9 rounded-[7px] object-cover transition-transform duration-500 group-hover:scale-105"
    />
    <span className="font-display text-[15px] font-bold tracking-[0.2em] text-ink">{BRAND.wordmark}</span>
  </Link>
);

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 font-mono text-[11px] tracking-wider ${className}`} data-testid="lang-toggle">
      <button
        data-testid="lang-de"
        onClick={() => setLang("de")}
        className={`px-1 transition-colors ${lang === "de" ? "text-bronze" : "text-stone hover:text-ink"}`}
      >
        DE
      </button>
      <span className="text-stone/40">/</span>
      <button
        data-testid="lang-en"
        onClick={() => setLang("en")}
        className={`px-1 transition-colors ${lang === "en" ? "text-bronze" : "text-stone hover:text-ink"}`}
      >
        EN
      </button>
    </div>
  );
};

export default function Header() {
  const { t } = useLang();
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      setHidden(y > last && y > 240);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="site-header"
        animate={{ y: hidden ? "-110%" : "0%" }}
        transition={{ duration: 0.45, ease: [0.33, 1, 0.68, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-paper/85 backdrop-blur-xl border-b border-border" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 h-[76px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `font-sans text-[12px] uppercase tracking-[0.14em] whitespace-nowrap transition-colors ${
                    isActive ? "text-bronze" : "text-ink/80 hover:text-bronze"
                  }`
                }
              >
                {t(UI.nav[item.key])}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-5">
            <LangToggle />
            <Link
              to="/contact"
              data-testid="header-cta"
              className="rounded-full bg-bronze text-paper px-6 py-2.5 text-[12px] font-semibold tracking-wide whitespace-nowrap hover:bg-[#f0be6e] transition-colors duration-300"
            >
              {t(UI.cta.letsStart)}
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-ink"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-paper lg:hidden flex flex-col"
          >
            <div className="h-[76px]" />
            <div className="flex-1 flex flex-col justify-center px-8 gap-1">
              {[{ to: "/", key: "home" }, ...navItems].map((item, i) => (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5 }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.key}`}
                    className="font-serif text-5xl text-ink block py-2"
                  >
                    {t(UI.nav[item.key])}
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <div className="px-8 pb-12 flex items-center justify-between border-t border-border pt-6">
              <LangToggle />
              <Link
                to="/contact"
                data-testid="mobile-header-cta"
                className="rounded-full bg-bronze text-paper px-6 py-3 text-sm font-semibold"
              >
                {t(UI.cta.letsStart)}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
